import React, { useState, useCallback, useEffect, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Map from '../components/Map/Map';
import MemoryModal from '../components/MemoryModal/MemoryModal';
import AuthModal from '../components/AuthModal/AuthModal';
import UserProfile from '../components/UserProfile/UserProfile';
import ThemePicker, { THEMES } from '../components/ThemePicker/ThemePicker';
import SnowAnimation from '../components/SnowAnimation/SnowAnimation';
import StarsAnimation from '../components/StarsAnimation/StarsAnimation';
import ConfettiAnimation from '../components/ConfettiAnimation/ConfettiAnimation';
import CreditsModal from '../components/CreditsModal/CreditsModal';
import MemoryLimitModal from '../components/MemoryLimitModal/MemoryLimitModal';
import LandmarkZoneModal from '../components/LandmarkZoneModal';
import ShareModal from '../components/ShareModal';
import {
  SendVelvynoteModal,
  VelvypalInbox,
  BuyMessagesModal,
  WriterProfileModal,
} from '../components/Velvypal';
import { useAuth } from '../hooks/useAuth';
import { useCredits } from '../hooks/useCredits';
import { useMemoryCapacity } from '../hooks/useMemoryCapacity';
import { useVelvypalConnections } from '../hooks/useVelvypalConnections';
import { useMessages } from '../hooks/useMessages';
import { createMemoryV2, getMemoryById, createLandmarkMemory } from '../utils/supabase';
import { getProfilePreview, sendVelvynote } from '../utils/velvypalApi';
import { checkLandmarkZone } from '../utils/landmarkZones';
import { logger } from '../utils/logger';

const VideoGenerationModal = lazy(() => import('../components/VideoGenerationModal/VideoGenerationModal'));

// Helper to get UI colors from theme
const getUIColors = (theme) => ({
  background: theme?.colors?.background || '#FAF7F2',
  labels: theme?.colors?.labels || '#4A4A4A',
  shadows: theme?.colors?.shadows || '#8B857A',
  romantic: theme?.colors?.romantic || '#E6B3C2',
  highlights: theme?.colors?.highlights || theme?.colors?.gold || '#F2C6A0',
  borders: theme?.colors?.borders || '#C5BBB0',
});


export default function MapPage() {
  const { memoryId: memoryIdParam } = useParams();
  const navigate = useNavigate();
  const memoryId = memoryIdParam?.replace(/\/$/, '') || null; // trim trailing slash

  const { user } = useAuth();
  const { credits, loading: creditsLoading, refreshCredits } = useCredits(user);
  const {
    currentCount,
    totalCapacity,
    canCreate,
    refresh: refreshMemoryCapacity
  } = useMemoryCapacity(user);
  const {
    newMessages: velvypalNewMessages,
    conversations: velvypalConversations,
    totalUnread: velvypalUnread,
    loading: velvypalLoading,
    refreshConnections,
  } = useVelvypalConnections(user);
  const {
    freeMessages,
    purchasedMessages,
    totalMessages,
    refreshMessages,
  } = useMessages(user);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showLocationCard, setShowLocationCard] = useState(false);
  const [showMemoryModal, setShowMemoryModal] = useState(false);
  const [showMemoryLimitModal, setShowMemoryLimitModal] = useState(false);
  const [showLandmarkZoneModal, setShowLandmarkZoneModal] = useState(false);
  const [landmarkZoneInfo, setLandmarkZoneInfo] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showVelvypalInbox, setShowVelvypalInbox] = useState(false);
  const [showSendVelvynote, setShowSendVelvynote] = useState(false);
  const [showBuyMessages, setShowBuyMessages] = useState(false);
  const [velvynoteRecipient, setVelvynoteRecipient] = useState(null);
  const [writerProfile, setWriterProfile] = useState(null);
  const [showWriterProfile, setShowWriterProfile] = useState(false);
  const [writerProfileLoading, setWriterProfileLoading] = useState(false);
  const [sendVelvynoteLoading, setSendVelvynoteLoading] = useState(false);
  const [showMyMemories, setShowMyMemories] = useState(false);
  const [showStarsAnimation, setShowStarsAnimation] = useState(false);
  const [showConfettiAnimation, setShowConfettiAnimation] = useState(false);
  const [newMemory, setNewMemory] = useState(null); // For immediate display
  const [isCreatingMemory, setIsCreatingMemory] = useState(false); // Loading state
  const [sharedMemory, setSharedMemory] = useState(null);
  const [sharedMemoryError, setSharedMemoryError] = useState(null);
  const [currentTheme, setCurrentTheme] = useState(() => {
    const saved = localStorage.getItem('mapTheme');
    return saved && THEMES[saved] ? THEMES[saved] : THEMES.default;
  });

  // Handle payment success/cancelled URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    const creditsAdded = params.get('credits');

    if (payment === 'success' && creditsAdded) {
      // Clear URL params
      window.history.replaceState({}, '', window.location.pathname);
      // Refresh credits balance
      refreshCredits();
      // Trigger celebration animation
      setShowConfettiAnimation(true);
      logger.info(`Payment successful! ${creditsAdded} credits added.`);
    } else if (payment === 'cancelled') {
      window.history.replaceState({}, '', window.location.pathname);
      logger.info('Payment cancelled');
    }
  }, [refreshCredits]);

  // Handle location click from map
  const handleLocationClick = useCallback(async (location) => {
    logger.debug('Location clicked:', location);
    setSelectedLocation(location);
    setWriterProfile(null);
    setShowWriterProfile(false);

    // Trigger confetti for newly discovered landmarks
    if (location.isLandmark && location.isNewDiscovery) {
      setShowConfettiAnimation(true);
    }

    // For memories: try to show writer profile, skip the memory card entirely
    if (location.isMemory && location.id) {
      // Own memory: tapping it opens the share modal directly.
      if (user && location.user_id === user.uid) {
        setShowLocationCard(false);
        setShowShareModal(true);
        return;
      }

      // For other users' memories: try to show writer profile
      if (user && location.user_id) {
        setShowLocationCard(false);
        setWriterProfileLoading(true);
        try {
          const result = await getProfilePreview(user, location.id);
          if (result.success && result.profile) {
            setWriterProfile(result.profile);
            setShowWriterProfile(true);
          }
          // If no profile (anonymous), just keep selection without card
        } catch (err) {
          logger.debug('Could not fetch profile preview:', err.message);
        } finally {
          setWriterProfileLoading(false);
        }
        return;
      }

      // Anonymous memory - just select without showing card
      setShowLocationCard(false);
      return;
    }

    // For landmarks: show LocationCard
    // For regular locations: just select (FAB area handles the UI)
    if (location.isLandmark) {
      setShowLocationCard(true);
    } else {
      setShowLocationCard(false);
    }
  }, [user]);

  // Toggle "My Memories" filter (now handled by tiles)
  const handleToggleMyMemories = useCallback(() => {
    const newValue = !showMyMemories;
    setShowMyMemories(newValue);
    // Trigger stars animation when entering "My Memories Only" mode
    if (newValue && user) {
      setShowStarsAnimation(true);
    }
    logger.debug(newValue ? '📍 Filtering to my memories' : '🌍 Showing all memories');
  }, [showMyMemories, user]);

  // Reset "My Memories" filter when user signs out
  useEffect(() => {
    if (!user && showMyMemories) {
      setShowMyMemories(false);
    }
  }, [user, showMyMemories]);

  // Fetch shared memory when visiting /m/:memoryId
  useEffect(() => {
    if (!memoryId) {
      setSharedMemory(null);
      setSharedMemoryError(null);
      return;
    }
    let cancelled = false;
    setSharedMemoryError(null);
    (async () => {
      try {
        const result = await getMemoryById(memoryId);
        if (cancelled) return;
        if (!result.success || !result.memory) {
          setSharedMemoryError(result.error || 'Memory not found');
          setSharedMemory(null);
          return;
        }
        setSharedMemory(result.memory);
      } catch (err) {
        if (!cancelled) {
          logger.error('Failed to fetch shared memory:', err);
          setSharedMemoryError('Failed to load memory');
          setSharedMemory(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [memoryId]);

  // When shared-memory fly completes: show writer profile or LocationCard
  const handleSharedMemoryFlyComplete = useCallback(async () => {
    if (!sharedMemory) return;
    const loc = {
      id: sharedMemory.id,
      lat: sharedMemory.lat,
      lng: sharedMemory.lng,
      text: sharedMemory.text,
      color: sharedMemory.color || '#E6B3C2',
      user_id: sharedMemory.user_id ?? null,
      isMemory: true,
    };
    setSelectedLocation(loc);
    setWriterProfile(null);
    setShowWriterProfile(false);

    // Check if this is someone else's non-anonymous memory
    const isOtherUserMemory = loc.id && user && loc.user_id && loc.user_id !== user.uid;

    if (isOtherUserMemory) {
      // For other users' memories: show writer profile card
      setShowLocationCard(false);
      setWriterProfileLoading(true);
      try {
        const result = await getProfilePreview(user, loc.id);
        if (result.success && result.profile) {
          setWriterProfile(result.profile);
          setShowWriterProfile(true);
        } else {
          setShowLocationCard(true);
        }
      } catch (err) {
        logger.debug('Could not fetch profile preview:', err.message);
        setShowLocationCard(true);
      } finally {
        setWriterProfileLoading(false);
      }
    } else {
      setShowLocationCard(true);
    }
  }, [sharedMemory, user]);

  // When user authenticates after clicking another user's memory, fetch profile and show writer profile card
  useEffect(() => {
    if (!selectedLocation?.isMemory || !selectedLocation?.id || !user) return;
    if (!selectedLocation.user_id || selectedLocation.user_id === user.uid) return;
    // Only fetch if LocationCard is showing (user clicked memory before authenticating)
    if (!showLocationCard) return;

    let cancelled = false;
    setWriterProfileLoading(true);

    (async () => {
      try {
        const result = await getProfilePreview(user, selectedLocation.id);
        if (cancelled) return;
        if (result.success && result.profile) {
          setWriterProfile(result.profile);
          setShowLocationCard(false);
          setShowWriterProfile(true);
        }
      } catch (err) {
        logger.debug('Could not fetch profile preview:', err.message);
      } finally {
        if (!cancelled) {
          setWriterProfileLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [selectedLocation, user, showLocationCard]);

  // Handle adding a memory via Supabase
  const handleAddMemory = useCallback(async (memoryData) => {
    setIsCreatingMemory(true);
    try {
      const userId = user?.uid || null;
      const isAnonymous = !user;

      let result;

      // Check if this is a landmark zone memory (requires payment)
      if (landmarkZoneInfo && user) {
        result = await createLandmarkMemory(
          user,
          memoryData.text,
          memoryData.lat,
          memoryData.lng,
          memoryData.color,
          landmarkZoneInfo.landmark.id,
          landmarkZoneInfo.gemCost
        );

        // Handle insufficient credits
        if (!result.success && result.error === 'INSUFFICIENT_CREDITS') {
          setShowMemoryModal(false);
          setLandmarkZoneInfo(null);
          // Re-show zone modal to allow buying more gems
          const zoneInfo = checkLandmarkZone(memoryData.lat, memoryData.lng);
          if (zoneInfo) {
            setLandmarkZoneInfo(zoneInfo);
            setShowLandmarkZoneModal(true);
          }
          return null;
        }
      } else {
        result = await createMemoryV2(
          memoryData.text,
          memoryData.lat,
          memoryData.lng,
          memoryData.color,
          userId,
          isAnonymous,
          user
        );
      }

      // Handle memory limit reached error from backend
      if (!result.success && result.error === 'MEMORY_LIMIT_REACHED') {
        setShowMemoryModal(false);
        setShowMemoryLimitModal(true);
        refreshMemoryCapacity();
        setLandmarkZoneInfo(null);
        return null;
      }

      if (result.success && result.memory) {
        setShowMemoryModal(false);
        setShowLocationCard(false);
        setLandmarkZoneInfo(null);

        // Show memory immediately
        setNewMemory(result.memory);
        logger.info('✨ Memory created via Supabase');

        // Open sharing right away — one tap, no hunting for a popup button.
        // ShareModal only renders for a signed-in user's own memory, so guard on user.
        if (user) {
          setSelectedLocation({
            id: result.memory.id,
            lat: memoryData.lat,
            lng: memoryData.lng,
            text: memoryData.text,
            color: memoryData.color,
            user_id: user.uid,
            isMemory: true,
          });
          setShowShareModal(true);
        }

        // Trigger confetti for landmark memories
        if (landmarkZoneInfo) {
          setShowConfettiAnimation(true);
          refreshCredits(); // Refresh gem balance after payment
        }

        // Refresh capacity for signed-in users
        if (user) {
          refreshMemoryCapacity();
        }

        return result.memory;
      }
    } catch (error) {
      logger.error('Failed to add memory:', error);
      throw error;
    } finally {
      setIsCreatingMemory(false);
    }
  }, [user, refreshMemoryCapacity, refreshCredits, landmarkZoneInfo]);

  // Handle successful slot purchase
  const handleSlotPurchaseSuccess = useCallback(() => {
    refreshMemoryCapacity();
    refreshCredits();
    // Trigger celebration animation
    setShowConfettiAnimation(true);
  }, [refreshMemoryCapacity, refreshCredits]);

  // Handle clicking "Become velvypals" button
  const handleBecomeVelvypals = useCallback(() => {
    if (!writerProfile) return;
    setVelvynoteRecipient(writerProfile);
    setShowSendVelvynote(true);
  }, [writerProfile]);

  // Handle sending a velvynote
  const handleSendVelvynote = useCallback(async (message) => {
    if (!user || !selectedLocation?.id || !message) return;

    setSendVelvynoteLoading(true);
    try {
      const result = await sendVelvynote(user, selectedLocation.id, message);
      if (result.success) {
        setShowSendVelvynote(false);
        setShowWriterProfile(false);
        setWriterProfile(null);
        setVelvynoteRecipient(null);
        refreshMessages();
        refreshConnections();
        // Show a brief success indicator
        setShowConfettiAnimation(true);
      } else {
        throw new Error(result.error || 'Failed to send velvynote');
      }
    } finally {
      setSendVelvynoteLoading(false);
    }
  }, [user, selectedLocation, refreshMessages, refreshConnections]);

  // Handle velvypal message purchase success
  const handleMessagesPurchaseSuccess = useCallback(() => {
    refreshMessages();
    refreshCredits();
    setShowConfettiAnimation(true);
  }, [refreshMessages, refreshCredits]);

  // Open memory creation modal (with capacity and zone check)
  const openMemoryModal = useCallback(() => {
    if (!selectedLocation) return;

    // For signed-in users, check capacity first
    if (user && !canCreate) {
      setShowMemoryLimitModal(true);
      return;
    }

    // Check if location is in a landmark's premium zone
    const zoneInfo = checkLandmarkZone(selectedLocation.lat, selectedLocation.lng);
    if (zoneInfo) {
      setLandmarkZoneInfo(zoneInfo);
      setShowLandmarkZoneModal(true);
      return;
    }

    setShowMemoryModal(true);
  }, [selectedLocation, user, canCreate]);

  // Handle landmark zone confirmation (user pays and continues to memory modal)
  const handleLandmarkZoneConfirm = useCallback(() => {
    // Close zone modal, open memory modal
    // The actual payment will happen in handleAddMemory
    setShowLandmarkZoneModal(false);
    setShowMemoryModal(true);
  }, []);

  const uiColors = getUIColors(currentTheme);
  const isChristmasTheme = currentTheme?.id === 'christmas';

  return (
    <div className="App relative w-full h-screen" style={{ backgroundColor: uiColors.background, margin: 0, padding: 0, position: 'fixed', top: 0, left: 0, height: '100dvh', overflow: 'hidden' }}>
        {/* Snow Animation - only for Christmas theme */}
        <SnowAnimation isActive={isChristmasTheme} />

        {/* Stars Animation - triggered when entering My Memories mode */}
        <StarsAnimation
          isActive={showStarsAnimation}
          onComplete={() => setShowStarsAnimation(false)}
        />

        {/* Confetti Animation - triggered on purchases */}
        <ConfettiAnimation
          isActive={showConfettiAnimation}
          onComplete={() => setShowConfettiAnimation(false)}
        />
        {/* Theme Picker - Top Left (smaller on mobile) */}
        <div className="absolute top-3 md:top-4 left-3 md:left-4 z-50 scale-[0.85] md:scale-100 origin-top-left">
          <ThemePicker currentTheme={currentTheme} onThemeChange={setCurrentTheme} />
        </div>

        {/* User Profile - Top Right (smaller on mobile) */}
        <div className="absolute top-3 md:top-4 right-3 md:right-4 z-50 scale-[0.85] md:scale-100 origin-top-right">
          <UserProfile
            onShowAuth={() => setShowAuthModal(true)}
            showMyMemories={showMyMemories}
            onToggleMyMemories={handleToggleMyMemories}
            onCreateVideo={() => setShowVideoModal(true)}
            credits={credits}
            creditsLoading={creditsLoading}
            onOpenCredits={() => setShowCreditsModal(true)}
            onShowInbox={() => setShowVelvypalInbox(true)}
            inboxUnreadCount={velvypalUnread}
          />
        </div>

        {/* Map */}
        <Map
          onLocationClick={handleLocationClick}
          newMemory={newMemory}
          onNewMemoryDisplayed={() => setNewMemory(null)}
          theme={currentTheme}
          showMyMemories={showMyMemories}
          user={user}
          initialView={memoryId ? 'world' : 'default'}
          flyToSharedMemory={sharedMemory}
          onSharedMemoryFlyComplete={handleSharedMemoryFlyComplete}
          selectedLocation={selectedLocation}
          showLocationCard={showLocationCard}
        />

        {/* FAB Area - Bottom Right - Always visible */}
        {!showLocationCard && (
          <div className="absolute bottom-6 md:bottom-8 right-3 md:right-6 flex items-center gap-3 z-10">
            {/* FAB - Add memory button (always visible, smaller on mobile) */}
            <div className="relative">
              {/* Pulse ring when location is selected */}
              {selectedLocation && !selectedLocation.isMemory && !selectedLocation.isLandmark && (
                <div
                  className="absolute inset-0 rounded-full animate-ping"
                  style={{
                    background: `linear-gradient(135deg, ${uiColors.romantic} 0%, ${uiColors.highlights} 100%)`,
                    opacity: 0.4
                  }}
                />
              )}
              <button
                className="relative w-12 h-12 md:w-16 md:h-16 rounded-full shadow-xl flex items-center justify-center text-2xl md:text-3xl transition-all transform hover:scale-110 active:scale-95"
                onClick={openMemoryModal}
                disabled={!selectedLocation || selectedLocation.isMemory || selectedLocation.isLandmark}
                style={{
                  background: (selectedLocation && !selectedLocation.isMemory && !selectedLocation.isLandmark)
                    ? `linear-gradient(135deg, ${uiColors.romantic} 0%, ${uiColors.highlights} 100%)`
                    : uiColors.shadows,
                  color: uiColors.labels,
                  boxShadow: (selectedLocation && !selectedLocation.isMemory && !selectedLocation.isLandmark)
                    ? `0 8px 24px rgba(139, 133, 122, 0.35), 0 4px 8px rgba(230, 179, 194, 0.4)`
                    : `0 4px 12px rgba(139, 133, 122, 0.25)`,
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  opacity: (selectedLocation && !selectedLocation.isMemory && !selectedLocation.isLandmark) ? 1 : 0.6,
                  cursor: (selectedLocation && !selectedLocation.isMemory && !selectedLocation.isLandmark) ? 'pointer' : 'not-allowed'
                }}
                aria-label="Add memory"
              >
                ✨
              </button>
            </div>
          </div>
        )}

        {/* Hint / Location info - above FAB */}
        {!showLocationCard && (
          <div
            className="absolute bottom-20 md:bottom-28 right-3 md:right-4 px-2 md:px-3 py-1.5 md:py-2 rounded-lg shadow-md text-xs md:text-sm max-w-44 md:max-w-52 text-center"
            style={{
              backgroundColor: 'rgba(250, 247, 242, 0.95)',
              color: uiColors.shadows,
              border: `1px solid rgba(197, 187, 176, 0.2)`
            }}
          >
            {selectedLocation && !selectedLocation.isMemory && !selectedLocation.isLandmark ? (
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] md:text-xs opacity-70">Tap ✨ to leave a memory</span>
                <span>📍 {selectedLocation.lat.toFixed(4)}, {selectedLocation.lng.toFixed(4)}</span>
              </div>
            ) : (
              <span>👆 Tap anywhere to select a location</span>
            )}
          </div>
        )}

        {/* Compact Location Card - Replaces FAB area when showing landmarks/new locations */}
        {showLocationCard && selectedLocation && (
          <div
            className="absolute bottom-6 md:bottom-8 right-3 md:right-6 backdrop-blur-sm p-3 md:p-4 rounded-2xl shadow-xl z-20"
            style={{
              backgroundColor: 'rgba(250, 247, 242, 0.98)',
              border: `2px solid rgba(197, 187, 176, 0.15)`,
              boxShadow: `0 8px 32px rgba(139, 133, 122, 0.3)`,
              maxWidth: 'calc(100vw - 24px)',
              width: selectedLocation.isLandmark ? '280px' : 'auto'
            }}
          >
            {/* Close button */}
            <button
              onClick={() => setShowLocationCard(false)}
              className="absolute top-2 right-2 w-6 h-6 md:w-7 md:h-7 rounded-full flex items-center justify-center text-sm md:text-base"
              style={{
                backgroundColor: 'rgba(139, 133, 122, 0.1)',
                color: uiColors.shadows
              }}
              aria-label="Close"
            >
              ×
            </button>

            {/* Compact layout for new locations */}
            {!selectedLocation.isLandmark && !selectedLocation.isMemory && (
              <div className="flex items-center gap-3 pr-6">
                <button
                  onClick={openMemoryModal}
                  className="w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center text-xl md:text-2xl shrink-0"
                  style={{
                    background: `linear-gradient(135deg, ${uiColors.romantic} 0%, ${uiColors.highlights} 100%)`,
                    boxShadow: `0 4px 12px rgba(230, 179, 194, 0.4)`
                  }}
                >
                  ✨
                </button>
                <div className="min-w-0">
                  <p className="text-xs md:text-sm font-medium truncate" style={{ color: uiColors.labels }}>
                    Leave a memory here
                  </p>
                  <p className="text-[10px] md:text-xs" style={{ color: uiColors.shadows }}>
                    {selectedLocation.lat.toFixed(4)}, {selectedLocation.lng.toFixed(4)}
                  </p>
                </div>
              </div>
            )}

            {/* Landmark card - more detailed */}
            {selectedLocation.isLandmark && (
              <div className="pr-6">
                <div className="flex items-start gap-2">
                  <span className="text-2xl md:text-3xl">{selectedLocation.icon}</span>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-sm md:text-base truncate" style={{ color: uiColors.labels }}>
                      {selectedLocation.name}
                    </h3>
                    <div className="flex items-center gap-1 mt-0.5">
                      <span
                        className="inline-block px-1.5 py-0.5 rounded-full text-[10px] md:text-xs font-bold uppercase"
                        style={{
                          backgroundColor: selectedLocation.rarity === 'legendary' ? 'rgba(255, 215, 0, 0.2)' :
                                           selectedLocation.rarity === 'epic' ? 'rgba(147, 112, 219, 0.2)' :
                                           'rgba(65, 105, 225, 0.2)',
                          color: selectedLocation.rarity === 'legendary' ? '#DAA520' :
                                 selectedLocation.rarity === 'epic' ? '#8A2BE2' :
                                 '#4169E1'
                        }}
                      >
                        {selectedLocation.rarity === 'legendary' ? '⭐' :
                         selectedLocation.rarity === 'epic' ? '💎' : '✨'}
                      </span>
                      {selectedLocation.city && (
                        <span className="text-[10px] md:text-xs" style={{ color: uiColors.shadows }}>
                          {selectedLocation.city}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {selectedLocation.description && (
                  <p className="text-[10px] md:text-xs mt-2 line-clamp-2" style={{ color: uiColors.shadows }}>
                    {selectedLocation.description}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Writer Profile Modal */}
        <WriterProfileModal
          isOpen={showWriterProfile || writerProfileLoading}
          profile={writerProfile}
          loading={writerProfileLoading}
          onClose={() => {
            setShowWriterProfile(false);
            setWriterProfile(null);
            setSelectedLocation(null);
          }}
          onSendMessage={handleBecomeVelvypals}
          totalMessages={totalMessages}
        />

        {/* Memory Creation Modal */}
        <MemoryModal
          isOpen={showMemoryModal}
          onClose={() => setShowMemoryModal(false)}
          onSubmit={handleAddMemory}
          location={selectedLocation || { lat: 0, lng: 0 }}
          user={user}
          loading={isCreatingMemory}
          onOpenAuth={() => {
            setShowMemoryModal(false);
            setShowAuthModal(true);
          }}
        />

        {/* Auth Modal */}
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onSuccess={(user) => {
            logger.info('Welcome!', user.displayName || 'User');
          }}
        />

        {/* Video Generation Modal - lazy loaded */}
        {showVideoModal && (
          <Suspense fallback={null}>
            <VideoGenerationModal
              isOpen={showVideoModal}
              onClose={() => setShowVideoModal(false)}
              user={user}
            />
          </Suspense>
        )}

        {/* Credits Modal */}
        <CreditsModal
          isOpen={showCreditsModal}
          onClose={() => setShowCreditsModal(false)}
          user={user}
          currentCredits={credits}
        />

        {/* Memory Limit Modal */}
        <MemoryLimitModal
          isOpen={showMemoryLimitModal}
          onClose={() => setShowMemoryLimitModal(false)}
          user={user}
          currentCount={currentCount}
          totalCapacity={totalCapacity}
          currentCredits={credits}
          onPurchaseSuccess={handleSlotPurchaseSuccess}
          onBuyCredits={() => setShowCreditsModal(true)}
        />

        {/* Landmark Zone Modal */}
        <LandmarkZoneModal
          isOpen={showLandmarkZoneModal}
          onClose={() => {
            setShowLandmarkZoneModal(false);
            setLandmarkZoneInfo(null);
          }}
          onConfirm={handleLandmarkZoneConfirm}
          onBuyCredits={() => setShowCreditsModal(true)}
          onOpenAuth={() => {
            setShowLandmarkZoneModal(false);
            setLandmarkZoneInfo(null);
            setShowAuthModal(true);
          }}
          user={user}
          currentCredits={credits}
          zoneInfo={landmarkZoneInfo}
        />

        {/* Share Modal - only for current user's memories */}
        {showShareModal && selectedLocation?.isMemory && user && selectedLocation.user_id === user.uid && (
          <ShareModal
            memory={selectedLocation}
            theme={currentTheme}
            user={user}
            onClose={() => setShowShareModal(false)}
          />
        )}

        {/* Velvypal Inbox Modal */}
        <VelvypalInbox
          isOpen={showVelvypalInbox}
          onClose={() => setShowVelvypalInbox(false)}
          user={user}
          newMessages={velvypalNewMessages}
          conversations={velvypalConversations}
          freeMessages={freeMessages}
          purchasedMessages={purchasedMessages}
          loading={velvypalLoading}
          onRefresh={refreshConnections}
          onBuyMessages={() => {
            setShowVelvypalInbox(false);
            setShowBuyMessages(true);
          }}
          refreshMessages={refreshMessages}
        />

        {/* Send Velvynote Modal */}
        <SendVelvynoteModal
          isOpen={showSendVelvynote}
          onClose={() => setShowSendVelvynote(false)}
          recipient={velvynoteRecipient}
          totalMessages={totalMessages}
          onSend={handleSendVelvynote}
          loading={sendVelvynoteLoading}
        />

        {/* Buy Messages Modal */}
        <BuyMessagesModal
          isOpen={showBuyMessages}
          onClose={() => setShowBuyMessages(false)}
          user={user}
          currentGems={credits}
          purchasedMessages={purchasedMessages}
          onPurchaseSuccess={handleMessagesPurchaseSuccess}
          onBuyGems={() => {
            setShowBuyMessages(false);
            setShowCreditsModal(true);
          }}
        />


        {/* Memory not found (share link) */}
        {sharedMemoryError && memoryId && (
          <div 
            className="absolute top-20 left-1/2 transform -translate-x-1/2 px-4 py-3 rounded-2xl shadow-lg z-30 flex flex-col sm:flex-row items-center gap-2 sm:gap-4"
            style={{
              background: 'rgba(250, 247, 242, 0.98)',
              color: uiColors.labels,
              border: `2px solid rgba(197, 187, 176, 0.3)`,
              boxShadow: `0 8px 24px rgba(139, 133, 122, 0.25)`
            }}
          >
            <span className="text-sm">😔 Memory not found</span>
            <button
              onClick={() => { setSharedMemoryError(null); navigate('/'); }}
              className="px-4 py-2 rounded-xl font-medium text-sm"
              style={{
                background: `linear-gradient(135deg, ${uiColors.romantic} 0%, ${uiColors.highlights} 100%)`,
                color: uiColors.labels,
                boxShadow: `0 4px 12px rgba(230, 179, 194, 0.4)`
              }}
            >
              Explore map
            </button>
          </div>
        )}

       
        {/* Animations */}
        <style>{`
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateX(-50%) translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateX(-50%) translateY(0);
            }
          }
        `}</style>
    </div>
  );
}

